import React, { useEffect, useState } from "react";
import { fetchApi } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone } from "lucide-react";

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  organizationId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PhoneSelectorProps {
  value?: string;
  onChange: (phoneId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export const PhoneSelector: React.FC<PhoneSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = "Select a phone number",
  className,
}) => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchApi("phone-numbers", "GET");

        if (response.ok && response.data) {
          setPhoneNumbers(response.data as PhoneNumber[]);
        } else {
          setError("Failed to fetch phone numbers");
        }
      } catch {
        setError("Failed to fetch phone numbers");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoneNumbers();
  }, []);

  const isValueDeleted =
    value && !phoneNumbers.some((phone) => phone.id === value);

  if (isLoading) {
    return (
      <div className={className}>
        <Select disabled>
          <SelectTrigger>
            <SelectValue placeholder="Loading phone numbers..." />
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className={className}>
        <div className="text-sm text-muted-foreground">
          No phone numbers available. Please add phone numbers to your
          organization first.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {isValueDeleted && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
          The selected phone number no longer exists. Please select a different
          one.
        </div>
      )}
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {phoneNumbers.map((phone) => (
            <SelectItem key={phone.id} value={phone.id}>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span>{phone.phoneNumber}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
